<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import useServices from "@/composables/useServices";
import useInternalLoyaltyProgram from "@/internal/modules/providers/composables/useLoyaltyProgram";
import { Chain } from "@/internal/modules/providers/interfaces/Chain";
import PaginateNav from "@/modules/trips/components/PaginateNav.vue";
import { reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import useChainLoyaltyProgram from "../composables/useChainLoyaltyProgam";

interface Props {
  chain: Chain;
}

const props = defineProps<Props>();
const toast = useToast();

const { getService } = useServices();

const {
  updateMutationAsync,
  updateMutationLoading,
  deleteMutationAsync,
  deleteMutationLoading,
  restoreMutationAsync,
  restoreMutationLoading,
} = useInternalLoyaltyProgram({
  immediate: false,
});

const {
  data,
  isLoading,
  refetch,
  createChainLoyalProgramMutationAsync,
  createChainLoyalProgramMutationLoading,
  page,
} = useChainLoyaltyProgram({
  immediate: true,
  params: {
    id: props.chain.id,
  },
  config: {
    errorHandling: false,
  },
});

const handlePage = (newPage: number | string) => {
  const pageNumber = Number(newPage);

  if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
    page.value = pageNumber;
    refetch();
  }
};

type Form = {
  name: string;
  id?: number;
};

const form = reactive<Form>({
  name: "",
});

const actionCreate = ref(false);
const handleActionCreate = () => {
  actionCreate.value = !actionCreate.value;
};

const handleCreate = async () => {
  if (!form.name) {
    toast.error("El nombre del programa de lealtad es requerido");
    return;
  }

  try {
    await createChainLoyalProgramMutationAsync({
      id: props.chain.id,
      name: form.name,
      serviceId: getService("Hotel")?.id!,
    });

    toast.success("Programa de lealtad creado correctamente");
    handleActionCreate();
    form.name = "";
    form.id = undefined;
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleUpdate = async () => {
  if (!form.name) {
    toast.error("El nombre del programa de lealtad es requerido");
    return;
  }

  try {
    await updateMutationAsync({
      id: form.id!,
      name: form.name,
      serviceId: getService("Hotel")?.id!,
    });

    toast.success("Programa de lealtad actualizado correctamente");
    handleActionCreate();
    form.name = "";
    form.id = undefined;
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleDelete = async (id: number) => {
  try {
    await deleteMutationAsync(id);

    toast.success("Programa de lealtad desactivado correctamente");
    form.name = "";
    form.id = undefined;
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const handleRestore = async (id: number) => {
  try {
    await restoreMutationAsync(id);

    toast.success("Programa de lealtad activado correctamente");
    form.name = "";
    form.id = undefined;
    refetch();
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};
</script>

<template>
  <ShadowBox class="bg-white">
    <div class="flex justify-between items-center mb-5">
      <h2 class="text-xl font-bold">Programas de lealtad</h2>
      <HasPermission
        :name-permission="'create-loyaltyPrograms-internal'"
        :entity-type="'App\\Models\\Chain'"
      >
        <StyledButton
          class="bg-blue-500 text-white"
          @click="handleActionCreate"
        >
          <span class="material-symbols-outlined">add </span>
          Agregar
        </StyledButton>
      </HasPermission>
    </div>

    <div v-for="loyaltyProgram in data?.data" :key="loyaltyProgram.id" class="">
      <div class="flex items-center justify-between mt-3">
        <div class="flex items-center gap-2">
          <span class="material-symbols-outlined">loyalty </span>
          <span class="font-bold text-xl">{{ loyaltyProgram.name }}</span>
        </div>
        <div class="flex items-center justify-between gap-3">
          <HasPermission
            :name-permission="'update-loyaltyPrograms-internal'"
            :entity-type="'App\\Models\\Chain'"
          >
            <button
              class="text-blue-500"
              @click="
                {
                  form.id = loyaltyProgram.id;
                  form.name = loyaltyProgram.name;
                  handleActionCreate();
                }
              "
              v-if="loyaltyProgram.active"
            >
              <span class="material-symbols-outlined">edit </span>
            </button>
          </HasPermission>

          <HasPermission
            :name-permission="'delete-loyaltyPrograms-internal'"
            :entity-type="'App\\Models\\Chain'"
          >
            <button
              class="text-red-500"
              v-if="loyaltyProgram.active"
              @click="
                () => {
                  handleDelete(loyaltyProgram.id);
                }
              "
            >
              <span class="material-symbols-outlined">delete </span>
            </button>
          </HasPermission>

          <HasPermission
            :name-permission="'restore-loyaltyPrograms-internal'"
            :entity-type="'App\\Models\\Chain'"
          >
            <button
              class="text-green-500"
              v-if="!loyaltyProgram.active"
              @click="
                () => {
                  handleRestore(loyaltyProgram.id);
                }
              "
            >
              <span class="material-symbols-outlined">restore </span>
            </button>
          </HasPermission>
        </div>
      </div>
    </div>

    <PaginateNav
      :page="page"
      :pagination="data"
      :data="data?.data || []"
      :handle-page="handlePage"
    />
  </ShadowBox>

  <!-- Modal create -->
  <ModalComponent
    :model-value="actionCreate"
    @update:model-value="
      () => {
        handleActionCreate();
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <form
          @submit.prevent="
            () => {
              form.id ? handleUpdate() : handleCreate();
            }
          "
        >
          <h1 class="font-bold text-xl mb-3">
            {{
              form.id
                ? "Actualizar programa de lealtad"
                : "Crear programa de lealtad"
            }}
          </h1>

          <StyledInput
            v-model="form.name"
            label="Nombre del programa de lealtad"
            class="mb-3"
          />

          <div class="flex justify-end">
            <StyledButton
              class="bg-blue-500 text-white"
              type="submit"
              :loading="
                createChainLoyalProgramMutationLoading || updateMutationLoading
              "
            >
              {{ form.id ? "Actualizar" : "Crear" }}
            </StyledButton>
          </div>
        </form>
      </div>
    </template>
  </ModalComponent>

  <LoadingModal :when="isLoading" />
</template>
